<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-950 pb-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-900 dark:text-gray-100">
      <div class="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Payment & Billing</h1>
          <p class="text-base text-gray-600 dark:text-gray-400 mt-2">Manage payments, invoices, and financial transactions</p>
        </div>
        <div class="flex items-center gap-2">
          <input v-model="search" type="text" class="form-input" placeholder="Search payments..." />
          <button class="btn btn-primary" @click="openAdd">Record Payment</button>
        </div>
      </div>

      <!-- Financial Statistics -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div class="card dark:bg-gray-900">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">Today's Revenue</p>
                <p class="text-2xl font-bold text-green-600">ETB {{ todayRevenue.toFixed(2) }}</p>
              </div>
              <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">Monthly Revenue</p>
                <p class="text-2xl font-bold text-blue-600">ETB {{ monthlyRevenue.toFixed(2) }}</p>
              </div>
              <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">Pending Payments</p>
                <p class="text-2xl font-bold text-yellow-600">{{ pendingPaymentsCount }}</p>
              </div>
              <div class="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">Total Transactions</p>
                <p class="text-2xl font-bold text-purple-600">{{ payments.length }}</p>
              </div>
              <div class="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Methods Chart -->
      <div class="card dark:bg-gray-900 mb-6">
        <div class="card-header dark:border-gray-800">
          <h2 class="text-xl font-semibold">Payment Methods Distribution</h2>
        </div>
        <div class="card-body">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div v-for="method in paymentMethods" :key="method.name" class="text-center">
              <div class="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center" :class="method.bgClass">
                <svg class="w-8 h-8" :class="method.iconClass" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="method.icon" />
                </svg>
              </div>
              <div class="font-medium">{{ method.name }}</div>
              <div class="text-sm text-gray-500">{{ method.count }} payments</div>
              <div class="text-lg font-bold" :class="method.textClass">ETB {{ method.amount.toFixed(2) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="card dark:bg-gray-900">
        <div class="card-body">
          <div class="overflow-x-auto">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th class="table-header-cell">Member</th>
                  <th class="table-header-cell">Type</th>
                  <th class="table-header-cell">Amount</th>
                  <th class="table-header-cell">Method</th>
                  <th class="table-header-cell">Date</th>
                  <th class="table-header-cell">Status</th>
                  <th class="table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr class="table-row" v-for="payment in filteredPayments" :key="payment.id">
                  <td class="table-cell">
                    <div class="flex items-center">
                      <div class="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                        <span class="text-sm font-medium text-gray-600">{{ payment.member.charAt(0) }}</span>
                      </div>
                      <span class="font-medium">{{ payment.member }}</span>
                    </div>
                  </td>
                  <td class="table-cell">
                    <span class="badge" :class="getTypeBadgeClass(payment.type)">
                      {{ payment.type }}
                    </span>
                  </td>
                  <td class="table-cell font-medium">ETB {{ payment.amount.toFixed(2) }}</td>
                  <td class="table-cell">{{ payment.method }}</td>
                  <td class="table-cell text-gray-500">{{ payment.date }}</td>
                  <td class="table-cell">
                    <span :class="getStatusBadgeClass(payment.status)">
                      {{ payment.status }}
                    </span>
                  </td>
                  <td class="table-cell">
                    <div class="flex gap-2">
                      <button class="btn btn-outline btn-sm" @click="viewDetails(payment)">View</button>
                      <button class="btn btn-info btn-sm" @click="generateInvoice(payment)">Invoice</button>
                      <button v-if="payment.status === 'Pending'" class="btn btn-success btn-sm" @click="markPaid(payment)">Mark Paid</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Add/Edit Payment Modal -->
      <div v-if="open" class="modal-overlay" @click="close">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="text-lg font-semibold">{{ form.id ? 'Edit Payment' : 'Record Payment' }}</h3>
          </div>
          <div class="modal-body">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="form-label">Member</label>
                <select v-model="form.member" class="form-input">
                  <option v-for="member in members" :key="member" :value="member">{{ member }}</option>
                </select>
              </div>
              <div>
                <label class="form-label">Payment Type</label>
                <select v-model="form.type" class="form-input">
                  <option value="Membership">Membership</option>
                  <option value="Class">Class</option>
                  <option value="Personal Training">Personal Training</option>
                  <option value="Equipment Rental">Equipment Rental</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label class="form-label">Amount (ETB)</label>
                <input v-model="form.amount" type="number" class="form-input" placeholder="0.00" step="0.01" />
              </div>
              <div>
                <label class="form-label">Payment Method</label>
                <select v-model="form.method" class="form-input">
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Mobile Money">Mobile Money</option>
                </select>
              </div>
              <div>
                <label class="form-label">Date</label>
                <input v-model="form.date" type="date" class="form-input" />
              </div>
              <div>
                <label class="form-label">Status</label>
                <select v-model="form.status" class="form-input">
                  <option value="Completed">Completed</option>
                  <option value="Pending">Pending</option>
                  <option value="Failed">Failed</option>
                </select>
              </div>
              <div class="md:col-span-2">
                <label class="form-label">Notes</label>
                <textarea v-model="form.notes" class="form-input" rows="3" placeholder="Payment notes..."></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-outline" @click="close">Cancel</button>
            <button class="btn btn-primary" @click="save">Save</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data: () => ({
    search: '',
    open: false,
    form: {
      id: null,
      member: '',
      type: 'Membership',
      amount: 0,
      method: 'Cash',
      date: new Date().toISOString().split('T')[0],
      status: 'Completed',
      notes: ''
    },
    members: ['John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Wilson', 'David Brown'],
    payments: [
      { id: 1, member: 'John Doe', type: 'Membership', amount: 500.00, method: 'Card', date: '2024-01-15', status: 'Completed', notes: 'Monthly membership renewal' },
      { id: 2, member: 'Jane Smith', type: 'Class', amount: 15.00, method: 'Cash', date: '2024-01-15', status: 'Completed', notes: 'Yoga class payment' },
      { id: 3, member: 'Mike Johnson', type: 'Personal Training', amount: 75.00, method: 'Bank Transfer', date: '2024-01-14', status: 'Completed', notes: '1-hour personal training session' },
      { id: 4, member: 'Sarah Wilson', type: 'Membership', amount: 300.00, method: 'Mobile Money', date: '2024-01-13', status: 'Pending', notes: 'Quarterly membership payment' },
      { id: 5, member: 'David Brown', type: 'Equipment Rental', amount: 25.00, method: 'Cash', date: '2024-01-12', status: 'Completed', notes: 'Towel and lock rental' }
    ]
  }),
  computed: {
    filteredPayments() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.payments;
      return this.payments.filter(payment =>
        payment.member.toLowerCase().includes(q) ||
        payment.type.toLowerCase().includes(q) ||
        payment.method.toLowerCase().includes(q)
      );
    },
    todayRevenue() {
      const today = new Date().toISOString().split('T')[0];
      return this.payments
        .filter(p => p.date === today && p.status === 'Completed')
        .reduce((sum, p) => sum + p.amount, 0);
    },
    monthlyRevenue() {
      const currentMonth = new Date().getFullYear() + '-' + String(new Date().getMonth() + 1).padStart(2, '0');
      return this.payments
        .filter(p => p.date.startsWith(currentMonth) && p.status === 'Completed')
        .reduce((sum, p) => sum + p.amount, 0);
    },
    pendingPaymentsCount() {
      return this.payments.filter(p => p.status === 'Pending').length;
    },
    paymentMethods() {
      const methods = {};
      this.payments.forEach(payment => {
        if (!methods[payment.method]) {
          methods[payment.method] = { name: payment.method, count: 0, amount: 0 };
        }
        methods[payment.method].count++;
        methods[payment.method].amount += payment.amount;
      });
      
      return Object.values(methods).map(method => ({
        ...method,
        bgClass: this.getMethodBgClass(method.name),
        iconClass: this.getMethodIconClass(method.name),
        icon: this.getMethodIcon(method.name),
        textClass: this.getMethodTextClass(method.name)
      }));
    }
  },
  methods: {
    openAdd() {
      this.form = { id: null, member: this.members[0], type: 'Membership', amount: 0, method: 'Cash', date: new Date().toISOString().split('T')[0], status: 'Completed', notes: '' };
      this.open = true;
    },
    close() {
      this.open = false;
    },
    save() {
      if (this.form.id) {
        const idx = this.payments.findIndex(p => p.id === this.form.id);
        if (idx > -1) this.payments.splice(idx, 1, { ...this.form });
        this.$bus.$emit('toast:show', { title: 'Payment updated', type: 'success' });
      } else {
        this.payments.push({ ...this.form, id: Date.now() });
        this.$bus.$emit('toast:show', { title: 'Payment recorded', type: 'success' });
      }
      this.open = false;
    },
    viewDetails(payment) {
      this.$bus.$emit('toast:show', { title: `Payment Details: ${payment.member}`, type: 'info' });
    },
    generateInvoice(payment) {
      this.$bus.$emit('toast:show', { title: `Invoice generated for ${payment.member}`, type: 'success' });
    },
    markPaid(payment) {
      payment.status = 'Completed';
      this.$bus.$emit('toast:show', { title: 'Payment marked as completed', type: 'success' });
    },
    getTypeBadgeClass(type) {
      switch (type) {
        case 'Membership':
          return 'badge badge-primary';
        case 'Class':
          return 'badge badge-info';
        case 'Personal Training':
          return 'badge badge-warning';
        case 'Equipment Rental':
          return 'badge badge-secondary';
        default:
          return 'badge badge-gray';
      }
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'Completed':
          return 'badge badge-success';
        case 'Pending':
          return 'badge badge-warning';
        case 'Failed':
          return 'badge badge-danger';
        default:
          return 'badge badge-info';
      }
    },
    getMethodBgClass(method) {
      switch (method) {
        case 'Cash':
          return 'bg-green-100';
        case 'Card':
          return 'bg-blue-100';
        case 'Bank Transfer':
          return 'bg-purple-100';
        case 'Mobile Money':
          return 'bg-orange-100';
        default:
          return 'bg-gray-100';
      }
    },
    getMethodIconClass(method) {
      switch (method) {
        case 'Cash':
          return 'text-green-600';
        case 'Card':
          return 'text-blue-600';
        case 'Bank Transfer':
          return 'text-purple-600';
        case 'Mobile Money':
          return 'text-orange-600';
        default:
          return 'text-gray-600';
      }
    },
    getMethodTextClass(method) {
      switch (method) {
        case 'Cash':
          return 'text-green-600';
        case 'Card':
          return 'text-blue-600';
        case 'Bank Transfer':
          return 'text-purple-600';
        case 'Mobile Money':
          return 'text-orange-600';
        default:
          return 'text-gray-600';
      }
    },
    getMethodIcon(method) {
      switch (method) {
        case 'Cash':
          return 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z';
        case 'Card':
          return 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z';
        case 'Bank Transfer':
          return 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z';
        case 'Mobile Money':
          return 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z';
        default:
          return 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z';
      }
    }
  }
}
</script>
